// src/context/PrizeContext.jsx
//
// Focused context for prize configuration derived data.
// Extracts the getPrizeData slice from the old DataContext god-hook.
//
import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useLeagueData } from './LeagueDataContext';
import { DEFAULT_PRIZE_DATA } from '../data/constants';

const PrizeContext = createContext(null);

/**
 * Hook to access computed prize configuration and winner data.
 * Use this instead of useData() when a component only needs prize info.
 */
export const usePrize = () => {
  const context = useContext(PrizeContext);
  if (!context) {
    throw new Error('usePrize must be used within a PrizeProvider');
  }
  return context;
};

/**
 * Derives prize data from raw league data:
 *   - Merges league-specific prize configuration over the defaults
 *   - Determines top-3 winners (paid-players-filtered when applicable)
 *   - Identifies the biggest rank-jumper from half-season comparison data
 *   - Collects monthly winners
 *
 * Depends on LeagueDataProvider being above it in the tree.
 */
export const PrizeProvider = ({ children }) => {
  const { data } = useLeagueData();

  // Compute derived prize data.
  // Memoised on the specific data slices that influence prize outcomes —
  // avoids re-computation when unrelated raw-data keys change.
  const prizeData = useMemo(() => {
    // Start from a safe copy of the immutable defaults
    const result = { ...DEFAULT_PRIZE_DATA };

    // Merge league-specific prize configuration when provided
    if (data?.prizeConfiguration) {
      result.mode = data.prizeConfiguration.mode || 'whatIf';

      if (result.mode === 'real') {
        if (data.prizeConfiguration.totalPool) result.totalPool = data.prizeConfiguration.totalPool;
        if (data.prizeConfiguration.firstPrize) result.firstPrize = data.prizeConfiguration.firstPrize;
        if (data.prizeConfiguration.secondPrize) result.secondPrize = data.prizeConfiguration.secondPrize;
        if (data.prizeConfiguration.thirdPrize) result.thirdPrize = data.prizeConfiguration.thirdPrize;
        if (data.prizeConfiguration.rankJumperPrize) result.rankJumperPrize = data.prizeConfiguration.rankJumperPrize;
        if (data.prizeConfiguration.monthlyPrize) result.monthlyPrize = data.prizeConfiguration.monthlyPrize;
      }

      if (data.prizeConfiguration.distribution) {
        result.distribution = { ...result.distribution, ...data.prizeConfiguration.distribution };
      }

      if (data.prizeConfiguration.customPrizes?.length > 0) {
        result.customPrizes = data.prizeConfiguration.customPrizes;
      }
    }

    // Build paid-players allow-list (empty = open league, no filtering)
    const paidPlayers = data?.['Paid Players']
      ? data['Paid Players'].map(player => player.Manager)
      : [];

    // Determine top-3 overall winners from the (optionally filtered) weekly-points table
    if (data?.weeklyPoints) {
      const filteredWeeklyPoints = paidPlayers.length > 0
        ? data.weeklyPoints.filter(player => paidPlayers.includes(player.Manager))
        : data.weeklyPoints;

      const topThree = [...filteredWeeklyPoints]
        .sort((a, b) => (b.Total || 0) - (a.Total || 0))
        .slice(0, 3);

      if (topThree.length > 0) result.firstPlace = topThree[0]?.Manager;
      if (topThree.length > 1) result.secondPlace = topThree[1]?.Manager;
      if (topThree.length > 2) result.thirdPlace = topThree[2]?.Manager;
    }

    // Identify the biggest rank jumper from half-season comparison data.
    // Always use canonical backend column names — Paid_* variants are not produced by the backend.
    if (data?.halfSeasonComparison) {
      const paidComparison = data['Half Season Comparison (Paid)'];
      const dataToUse =
        paidPlayers.length > 0 && paidComparison ? paidComparison : data.halfSeasonComparison;

      const filteredComparison = paidPlayers.length > 0
        ? dataToUse.filter(player => paidPlayers.includes(player.Manager))
        : dataToUse;

      const rankDiffKey = 'Rank_Difference';
      const midSeasonRankKey = 'Mid_Season_Rank';
      const endSeasonRankKey = 'End_Season_Rank';

      const rankJumpers = [...filteredComparison]
        .filter(manager => manager[rankDiffKey] > 0) // Positive diff = improvement
        .sort((a, b) => b[rankDiffKey] - a[rankDiffKey]);

      if (rankJumpers.length > 0) {
        const topJumper = rankJumpers[0];
        // Backend guarantees correct numeric types — no coercion needed
        const firstHalfRank = topJumper[midSeasonRankKey] ?? 0;
        const finalRank = topJumper[endSeasonRankKey] ?? 0;
        const rankDiff = topJumper[rankDiffKey] ?? 0;

        result.rankJumpWinner = topJumper.Manager;
        result.rankJumpValue = rankDiff;
        result.rankJumperGW19Rank = firstHalfRank || result.rankJumperGW19Rank;
        result.rankJumperFinalRank = finalRank || result.rankJumperFinalRank;
      }
    }

    // Collect monthly top-performer winners (paid-players-filtered when applicable)
    if (data?.monthlyTopPerformers) {
      result.monthlyWinners = paidPlayers.length > 0
        ? data.monthlyTopPerformers.filter(player => paidPlayers.includes(player.Manager))
        : data.monthlyTopPerformers;
    }

    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.prizeConfiguration, data?.weeklyPoints, data?.halfSeasonComparison, data?.monthlyTopPerformers, data?.['Paid Players']]);

  // Stable callback wrapper matches the getPrizeData() API from the original god-hook
  const getPrizeData = useCallback(() => prizeData, [prizeData]);

  const value = useMemo(() => ({
    prizeData,
    getPrizeData,
  }), [prizeData, getPrizeData]);

  return (
    <PrizeContext.Provider value={value}>
      {children}
    </PrizeContext.Provider>
  );
};

export default PrizeContext;
